import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaLightbulb, FaChalkboardTeacher, FaVial, FaCube, FaBoxOpen, 
  FaBullhorn, FaFlask, FaLayerGroup, FaTree, FaCreditCard, 
  FaFileAlt, FaSearch, FaGlobe, FaChartLine, FaHandshake,
  FaGraduationCap, FaBook, FaTag, FaUserPlus, FaInfoCircle,
  FaEnvelope, FaPhone, FaLinkedin, FaTwitter, FaBars, FaTimes,
  FaCheckCircle, FaStar, FaHome, FaDollarSign, FaUsers, FaWallet,
  FaArrowRight, FaCog, FaQuestionCircle
} from 'react-icons/fa';
import HeroHeader from '../component/HeroHeader';
import Navbar from '../component/Navbar';
// Theme colors
const primarysky = 'rgb(43, 147, 208)';
const yellowAccent = '#fbbf24';

const MembershipForm = () => {
  // State for mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // State for membership tabs (national/international)
  const [membershipTab, setMembershipTab] = useState('national');
  
  // State for payment form type
  const [paymentType, setPaymentType] = useState('national');
  
  // State for form preview
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Refs for sections (used for scrolling and intersection observer)
  const homeRef = useRef(null);
  const servicesRef = useRef(null);
  const membershipRef = useRef(null);
  const howToApplyRef = useRef(null);
  const formRef = useRef(null);
  const paymentRef = useRef(null);
  const highlightsRef = useRef(null);
  const testimonialRef = useRef(null);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.fade-in-section').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Floating elements for hero
  useEffect(() => {
    const container = document.getElementById('floating-elements');
    if (!container) return;
    const colors = [
      'rgba(255, 235, 59, 0.15)',
      'rgba(76, 175, 80, 0.15)',
      'rgba(33, 150, 243, 0.15)',
      'rgba(156, 39, 176, 0.15)',
      'rgba(0, 229, 255, 0.15)'
    ];
    for (let i = 0; i < 25; i++) {
      const el = document.createElement('div');
      const size = Math.random() * 70 + 10;
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.left = `${Math.random() * 100}%`;
      el.style.top = `${Math.random() * 100}%`;
      el.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      el.style.animation = `float ${Math.random() * 30 + 15}s ${Math.random() * 10}s infinite ease-in-out`;
      el.style.filter = `blur(${Math.random() * 3}px)`;
      el.className = 'floating-element absolute rounded-full';
      container.appendChild(el);
    }
  }, []);

  // Smooth scroll to section
  const scrollToSection = (ref) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSidebarOpen(false); // Close mobile sidebar after click
    }
  };

  // Service boxes data
  const services = [
    { icon: FaLightbulb, title: 'Technology Transfer & Upgradation', link: 'https://wasmeinfo.org/services.html#tech-transfer' },
    { icon: FaChalkboardTeacher, title: 'Skill Dev & Enterprise Development Program', link: 'https://wasmeinfo.org/services.html#skill-dev' },
    { icon: FaVial, title: 'Digitalization in MSMEs (IT, Health, Tourism)', link: 'https://wasmeinfo.org/services.html#testing-quality' },
    { icon: FaCube, title: 'Prototype & Product Design', link: 'https://wasmeinfo.org/services.html#prototype-design' },
    { icon: FaBoxOpen, title: 'Packaging Solutions', link: 'https://wasmeinfo.org/services.html#packaging' },
    { icon: FaBullhorn, title: 'Marketing & Export Promotion', link: 'https://wasmeinfo.org/services.html#marketing-export' },
    { icon: FaFlask, title: 'Quality Control, Testing & setting up of Labs', link: 'https://wasmeinfo.org/services.html#labs' },
    { icon: FaLayerGroup, title: 'Cluster Dev & CFC Setup', link: 'https://wasmeinfo.org/services.html#cluster-dev' },
    { icon: FaTree, title: 'Rural Industrialization (Artisans / Micro Enterprises)', link: 'https://wasmeinfo.org/services.html#rural-industry' },
    { icon: FaCreditCard, title: 'Credit & Financial Services', link: 'https://wasmeinfo.org/services.html#rural-industry' },
    { icon: FaFileAlt, title: 'Regulatory & Capacity Building', link: 'https://wasmeinfo.org/services.html#rural-industry' },
    { icon: FaSearch, title: 'Research & Policy Advocacy', link: 'https://wasmeinfo.org/services.html#rural-industry' },
  ];

  // Pricing tables data
  const nationalPricing = [
    { category: 'General Members', items: [
      { desc: 'Government Undertakings, Ministries, Departments, Public Sector Undertakings, Semi Govt org, Export Promotion Councils, Trade Councils, Financial Institutions, Banks, NBFCs, Enterprise Development Org, National MSME Promotion Org, etc.', price: '₹3,00,000' },
      { desc: 'National Chambers/Industry Associations/MSME Associations', price: '₹86,000' },
    ]},
    { category: 'Associate Members', items: [
      { desc: 'Sectoral/Local Industry Associations / Chambers', price: '₹86,000' },
      { desc: 'Corporations, Consulting Firms, NGOs, Partnership, Proprietorship, LLP, Research Institutes, Technical Institutes, Universities, Individual Consultants, Experts etc.', price: '₹47,000' },
    ]},
    { category: 'MSMEs', items: [
      { desc: 'Micro (Turnover < ₹10 Cr)', price: '₹34,400' },
      { desc: 'Small (Turnover ₹10-100 Cr)', price: '₹51,600' },
      { desc: 'Medium (Turnover > ₹100 Cr)', price: '₹1,07,500' },
    ]},
    { category: 'Permanent Membership', items: [
      { desc: 'Any General Member or Associate Member who is willing to be Permanent Member of WASME', price: '₹17,20,000' },
    ]},
  ];

  const internationalPricing = [
    { category: 'General Members', items: [
      { desc: 'Government Undertakings, Ministries, Departments, Public Sector Undertakings, Semi Govt org, Export Promotion Councils, Trade Councils, Financial Institutions, Banks, NBFCs, Enterprise Development Org, National MSME Promotion Org, etc.', price: '$3,500' },
      { desc: 'National Chambers/Industry Associations/MSME Associations', price: '$1,000' },
      { desc: 'International & Regional Federations/ Associations', price: '$5,000' },
    ]},
    { category: 'Associate Members', items: [
      { desc: 'Sectoral/ local level Industry Association/ Chambers', price: '$1,000' },
      { desc: 'Corporations, Consulting Firms, NGOs, Partnership, Proprietorship, LLP, Research Institutes, Technical Institutes, Universities, Individual Consultants, Experts etc.', price: '$550' },
    ]},
    { category: 'MSMEs', items: [
      { desc: 'Micro (Turnover < $1M)', price: '$400' },
      { desc: 'Small (Turnover $1M-$12M)', price: '$600' },
      { desc: 'Medium (Turnover > $12M)', price: '$1,250' },
    ]},
    { category: 'Permanent Membership', items: [
      { desc: 'Any General Member or Associate Member who is willing to be Permanent Member of WASME', price: '$20,000' },
    ]},
  ];

  // Bank details
  const bankDetails = {
    national: {
      bank: 'CANARA BANK',
      accountName: 'World Association for Small and Medium Enterprises (WASME)',
      accountNo: '88853070000808',
      ifsc: 'CNRB0018778',
      branch: 'MSME Branch Sector -18 Noida-201301',
      gst: '09AAATW0026A1ZI',
      instructions: 'Cheque/Demand Draft/RTGS payable at Noida, India. Online/UPI available via QR code.'
    },
    international: {
      bank: 'Bank of Baroda',
      accountName: 'WASME',
      accountNo: '25620200001058',
      ifsc: 'BARB0SSIGAZ',
      swift: 'BARBINBBNOI',
      branch: 'MSME Branch, P-5 Ocean Plaza, Sector – 18, Noida, Uttar Pradesh',
      instructions: 'Pay in favor of WASME. Wire Transfer accepted outside India.'
    }
  };

  // Form handling
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePreview = (e) => {
    e.preventDefault();
    const form = document.getElementById('membershipForm');
    if (form.checkValidity()) {
      setShowPreview(true);
    } else {
      form.reportValidity();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would integrate EmailJS
    // For demo, just show success
    setShowSuccess(true);
    setShowPreview(false);
  };

  // Sidebar navigation items
  const navItems = [
    { icon: FaHome, label: 'Home', ref: homeRef },
    { icon: FaCog, label: 'Services', ref: servicesRef },
    { icon: FaUsers, label: 'Membership', ref: membershipRef },
    { icon: FaArrowRight, label: 'How to Apply', ref: howToApplyRef },
    { icon: FaFileAlt, label: 'Application Form', ref: formRef },
    { icon: FaDollarSign, label: 'Payment', ref: paymentRef },
    { icon: FaStar, label: 'Highlights', ref: highlightsRef },
    { icon: FaQuestionCircle, label: 'Testimonial', ref: testimonialRef },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
     <Navbar/>

      {/* Mobile Sidebar Drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <img src="/img/WASME Logo Final White LIne.png" alt="WASME" className="h-8" />
              <button onClick={() => setSidebarOpen(false)}><FaTimes size={20} /></button>
            </div>
            <ul className="space-y-4">
              {navItems.map((item, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => scrollToSection(item.ref)}
                    className="flex items-center space-x-3 w-full text-left py-2 px-3 rounded-lg hover:bg-sky-50 hover:text-sky-600 transition-colors"
                  >
                    <item.icon className="text-gray-500" />
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Main Content with Sidebar Layout */}
      <div className="flex">
        {/* Desktop Sidebar - fixed on left */}
        <aside className="hidden md:block fixed left-0 top-[60px] h-[calc(100vh-60px)] w-64 bg-white border-r border-gray-200 shadow-sm z-20 overflow-y-auto">
          <div className="p-5">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Navigation</h3>
            <ul className="space-y-1">
              {navItems.map((item, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => scrollToSection(item.ref)}
                    className="flex items-center space-x-3 w-full text-left py-3 px-4 rounded-lg text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors group"
                  >
                    <item.icon className="text-gray-400 group-hover:text-sky-500" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>

            {/* Quick contact or extra info */}
            <div className="mt-8 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400 mb-2">Need help?</p>
              <a href="mailto:wasme@wasmeinfo.org" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-sky-600 mb-2">
                <FaEnvelope size={14} />
                <span>wasme@wasmeinfo.org</span>
              </a>
              <a href="tel:+919560685555" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-sky-600">
                <FaPhone size={14} />
                <span>+91 9560685555</span>
              </a>
            </div>
          </div>
        </aside>

        {/* Main Content - with left margin on desktop to accommodate sidebar */}
        <main className="flex-1 md:ml-64 min-w-0">
          {/* Hero Section */}
          <div ref={homeRef} id="home">
            <HeroHeader
              title="Become a Proud Member of"
              highlight="WASME"
              subtitle="Join a Global Network of Entrepreneurs and Innovators. Unlock exclusive opportunities for your MSME."
              backgroundImage="/img/wasme_office2.jpeg"
              height="50vh"
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Services Grid */}
            <section id="services" ref={servicesRef} className="mb-20 fade-in-section">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">WASME Services to Members</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {services.map((service, idx) => (
                  <a
                    key={idx}
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 hover:bg-gradient-to-br hover:from-sky-600 hover:to-sky-700 hover:text-white"
                  >
                    <service.icon className="text-4xl mb-3 text-sky-500 group-hover:text-white transition-colors" />
                    <h3 className="font-semibold text-gray-800 group-hover:text-white">{service.title}</h3>
                  </a>
                ))}
              </div>
            </section>

            {/* Membership Plans */}
            <section id="membership" ref={membershipRef} className="mb-20 fade-in-section">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Membership Plans</h2>
              <p className="text-center text-gray-600 mb-8">Choose the plan that fits your organization</p>

              {/* Tabs */}
              <div className="flex justify-center mb-8">
                <div className="inline-flex rounded-full bg-gray-200 p-1">
                  <button
                    onClick={() => setMembershipTab('national')}
                    className={`px-6 py-2 rounded-full font-medium transition-colors ${membershipTab === 'national' ? 'bg-white text-sky-600 shadow' : 'text-gray-700'}`}
                  >
                    National (INR)
                  </button>
                  <button
                    onClick={() => setMembershipTab('international')}
                    className={`px-6 py-2 rounded-full font-medium transition-colors ${membershipTab === 'international' ? 'bg-white text-sky-600 shadow' : 'text-gray-700'}`}
                  >
                    International (USD)
                  </button>
                </div>
              </div>

              {/* Pricing Table */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                <table className="w-full text-sm text-left">
                  <thead className="bg-sky-600 text-white">
                    <tr>
                      <th className="px-6 py-4">Category</th>
                      <th className="px-6 py-4">Annual Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(membershipTab === 'national' ? nationalPricing : internationalPricing).map((section, idx) => (
                      <React.Fragment key={idx}>
                        <tr className="bg-gray-100 font-semibold">
                          <td colSpan="2" className="px-6 py-3 text-gray-700">{section.category}</td>
                        </tr>
                        {section.items.map((item, i) => (
                          <tr key={i} className="border-b border-gray-200 hover:bg-sky-50">
                            <td className="px-6 py-3 text-gray-700">{item.desc}</td>
                            <td className="px-6 py-3 font-medium text-sky-600 whitespace-nowrap">{item.price}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Membership Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                {[
                  'Access to global MSME database',
                  'Priority invitation to WASME events',
                  'Monthly newsletter with industry insights',
                  'Discounts on training programs',
                  'WASME certification for members',
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-gray-700">
                    <FaCheckCircle className="text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* How to Apply */}
            <section id="how-to-apply" ref={howToApplyRef} className="mb-20 fade-in-section">
              <div className="bg-gradient-to-r from-sky-600 to-indigo-700 rounded-3xl p-10 text-white">
                <h2 className="text-3xl font-bold text-center mb-2">How to Apply for Membership</h2>
                <p className="text-center opacity-90 mb-8">Joining WASME is simple and opens doors to countless opportunities for your business.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { step: '1', title: 'Choose Category', desc: 'Select your appropriate membership category' },
                    { step: '2', title: 'Fill Form', desc: 'Complete the online application form' },
                    { step: '3', title: 'Make Payment', desc: 'Submit payment via bank transfer, cheque or demand draft' },
                    { step: '4', title: 'Get Approved', desc: 'Receive confirmation and start enjoying WASME Services' },
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <div className="w-12 h-12 bg-white text-sky-600 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">{item.step}</div>
                      <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                      <p className="text-sm opacity-80">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-8">
                  <button onClick={() => scrollToSection(formRef)} className="inline-block px-8 py-3 bg-white text-sky-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    Apply Now
                  </button>
                </div>
              </div>
            </section>

            {/* Application Form */}
            <section id="application-form" ref={formRef} className="mb-20 fade-in-section">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">WASME Membership Application</h2>
                <p className="text-center text-gray-600 mb-8">Please fill out the form below to apply</p>

                {showSuccess ? (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6">
                    Your application has been submitted successfully! We will contact you shortly.
                  </div>
                ) : showPreview ? (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4">Preview Your Application</h3>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      {Object.entries(formData).map(([key, value]) => (
                        key !== 'certify' && value && (
                          <div key={key} className="mb-2"><strong>{key}:</strong> {value}</div>
                        )
                      ))}
                    </div>
                    <div className="flex justify-end space-x-4 mt-4">
                      <button onClick={() => setShowPreview(false)} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">Back to Edit</button>
                      <button onClick={handleSubmit} className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700">Confirm & Submit</button>
                    </div>
                  </div>
                ) : (
                  <form id="membershipForm" onSubmit={handlePreview}>
                    {/* Basic Information */}
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                        <input type="text" name="country" required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CEO/Chairman/President</label>
                        <input type="text" name="position" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <select name="title" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange}>
                          <option value="">Select Title</option>
                          <option value="Mr.">Mr.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Prof.">Prof.</option>
                          <option value="Dr.">Dr.</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                        <input type="text" name="firstName" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                        <input type="text" name="middleName" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                        <input type="text" name="lastName" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Organization/Department</label>
                        <input type="text" name="organization" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                    </div>

                    {/* General Information */}
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">General Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                        <input type="text" name="orgName" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Establishment *</label>
                        <input type="date" name="establishmentDate" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number (if any)</label>
                        <input type="text" name="registrationNumber" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Main line of Business *</label>
                        <textarea name="mainBusiness" rows="3" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange}></textarea>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Countries we have business/export/offices</label>
                        <textarea name="businessCountries" rows="2" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange}></textarea>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Key Business Interest *</label>
                        <textarea name="keyInterest" rows="3" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange}></textarea>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                        <textarea name="address" rows="2" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange}></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                        <input type="text" name="city" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City Code</label>
                        <input type="text" name="cityCode" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                        <input type="text" name="state" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pin/Zip Code *</label>
                        <input type="text" name="zipCode" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country (contact) *</label>
                        <input type="text" name="contactCountry" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
                        <input type="text" name="countryCode" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile (1) *</label>
                        <input type="tel" name="mobile1" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile (2)</label>
                        <input type="tel" name="mobile2" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input type="email" name="email" required className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                        <input type="url" name="website" className="w-full px-4 py-2 border rounded-lg" onChange={handleInputChange} />
                      </div>
                    </div>

                    {/* Certification */}
                    <div className="mb-6">
                      <label className="flex items-center space-x-2">
                        <input type="checkbox" name="certify" required className="rounded text-sky-600" onChange={handleInputChange} />
                        <span className="text-sm text-gray-700">I/we hereby certify that the above details furnished are accurate and are to the best of my knowledge.</span>
                      </label>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4">
                      <button type="button" onClick={handlePreview} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Preview</button>
                      <button type="submit" className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700">Submit Application</button>
                    </div>
                  </form>
                )}
              </div>
            </section>

            {/* Payment Details */}
            <section id="payment-details" ref={paymentRef} className="mb-20 fade-in-section">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Payment Details</h2>

                {/* Payment Type Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="inline-flex rounded-full bg-gray-200 p-1">
                    <button
                      onClick={() => setPaymentType('national')}
                      className={`px-6 py-2 rounded-full font-medium transition-colors ${paymentType === 'national' ? 'bg-white text-sky-600 shadow' : 'text-gray-700'}`}
                    >
                      National (India)
                    </button>
                    <button
                      onClick={() => setPaymentType('international')}
                      className={`px-6 py-2 rounded-full font-medium transition-colors ${paymentType === 'international' ? 'bg-white text-sky-600 shadow' : 'text-gray-700'}`}
                    >
                      International
                    </button>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="bg-sky-50 p-6 rounded-lg mb-6">
                  <h3 className="font-semibold text-lg mb-2">Bank Details</h3>
                  <div className="space-y-1 text-gray-700">
                    <p><span className="font-medium">Bank Name:</span> {bankDetails[paymentType].bank}</p>
                    <p><span className="font-medium">A/C Name:</span> {bankDetails[paymentType].accountName}</p>
                    <p><span className="font-medium">Account No:</span> {bankDetails[paymentType].accountNo}</p>
                    <p><span className="font-medium">IFSC Code:</span> {bankDetails[paymentType].ifsc}</p>
                    {paymentType === 'international' && <p><span className="font-medium">SWIFT Code:</span> {bankDetails[paymentType].swift}</p>}
                    <p><span className="font-medium">Branch:</span> {bankDetails[paymentType].branch}</p>
                    {paymentType === 'national' && <p><span className="font-medium">GST No.:</span> {bankDetails[paymentType].gst}</p>}
                    <p className="mt-2 text-sm"><span className="font-medium">Instructions:</span> {bankDetails[paymentType].instructions}</p>
                  </div>
                </div>

                {/* Payment Form */}
                <form id="paymentForm" className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Membership Amount *</label>
                    <input type="text" name="membershipAmount" required className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Bank Name & Branch *</label>
                    <input type="text" name="bankName" required className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cheque/Demand Draft No. *</label>
                    <input type="text" name="draftNo" required className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Issue *</label>
                    <input type="text" name="date" placeholder="DD/MM/YYYY" required className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div className="md:col-span-2 flex justify-end space-x-4">
                    <button type="button" className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Preview Payment</button>
                    <button type="submit" className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700">Submit Payment</button>
                  </div>
                </form>
              </div>
            </section>

            {/* Highlights */}
            <section id="highlights" ref={highlightsRef} className="mb-20 fade-in-section">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-800">Highlights</h2>
                <p className="text-gray-600 max-w-2xl mx-auto mt-2">
                  <span className="font-semibold">NITD</span> is an active member of WASME, committed to MSMEs development through international collaborations, policy advocacy, and knowledge exchange.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { icon: FaGlobe, title: 'International Collaborations', desc: 'We foster global partnerships to enhance MSMEs growth and create opportunities for cross-border cooperation.' },
                  { icon: FaBullhorn, title: 'Policy Advocacy', desc: 'Actively engaged in shaping policies that support MSMEs development at national and international levels.' },
                  { icon: FaBook, title: 'Knowledge Exchange', desc: 'Facilitating the sharing of best practices, research, and innovative solutions to empower MSMEs.' },
                ].map((item, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition border border-gray-100 text-center">
                    <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="text-3xl text-sky-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonial / Benefits */}
            <section id="testimonial" ref={testimonialRef} className="mb-20 fade-in-section">
              <div className="bg-gradient-to-br from-sky-50 to-white rounded-3xl p-10 shadow-xl border border-gray-100">
                <div className="flex flex-col lg:flex-row items-center gap-10">
               
                  <div className="lg:w-2/3">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">Unlock Global Opportunities with WASME</h2>
                    <p className="text-gray-600 mb-6">Join our network of thriving MSMEs and gain access to international markets, partnerships, and growth opportunities.</p>
                    <div className="bg-white p-4 rounded-lg shadow mb-6">
                      <p className="italic text-gray-700">"Since joining WASME, we have expanded our global reach and partnered with MSMEs across countries!"</p>
                      <p className="mt-2 font-semibold text-sky-600">— Proud WASME Member</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { icon: FaGlobe, title: 'Global Networking', desc: 'Connect with industry leaders worldwide.' },
                        { icon: FaBullhorn, title: 'Advocacy & Policy', desc: 'Shape MSME-friendly policies.' },
                        { icon: FaChartLine, title: 'Business Promotion', desc: 'Showcase your brand globally.' },
                        { icon: FaGraduationCap, title: 'Training & Development', desc: 'Exclusive workshops and webinars.' },
                        { icon: FaBook, title: 'Research & Insights', desc: 'Access valuable market reports.' },
                        { icon: FaTag, title: 'Exclusive Discounts', desc: 'Special rates for conferences and events.' },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <item.icon className="text-sky-600 mt-1 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold">{item.title}</h4>
                            <p className="text-sm text-gray-600">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center space-x-4 mt-8">
                  <button onClick={() => scrollToSection(formRef)} className="px-6 py-3 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 transition flex items-center">
                    <FaUserPlus className="mr-2" /> Join Now
                  </button>
                  <a href="https://wasmeinfo.org/" target="_blank" rel="noopener noreferrer" className="px-6 py-3 border border-sky-600 text-sky-600 rounded-lg font-semibold hover:bg-sky-50 transition flex items-center">
                    <FaInfoCircle className="mr-2" /> Learn More
                  </a>
                </div>
              </div>
            </section>
          </div>

          {/* Footer */}
          <footer className="bg-gray-900 text-gray-300 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="flex justify-center space-x-4 mb-4">
                <a href="mailto:wasme@wasmeinfo.org" className="hover:text-white"><FaEnvelope size={20} /></a>
                <a href="tel:+919560685555" className="hover:text-white"><FaPhone size={20} /></a>
                <a href="https://www.linkedin.com/company/wasmeinfo-org/" target="_blank" rel="noopener noreferrer" className="hover:text-white"><FaLinkedin size={20} /></a>
                <a href="https://x.com/Wasmeinfoorg" target="_blank" rel="noopener noreferrer" className="hover:text-white"><FaTwitter size={20} /></a>
              </div>
              <p className="text-sm">© 2025 WASME. All rights reserved. Designed and Developed by <a href="https://netcoreinfo.com" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Netcoreinfo</a></p>
            </div>
          </footer>
        </main>
      </div>

    

      {/* Add keyframe animations */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .fade-in-section {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .fade-in-section.fade-in-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default MembershipForm;